// Live feeds (api.ts FeedService): what the world is doing right now, while the date is the as-of date.
//
//   quakes · alerts · events · chokepoints · launches · headlines     (data/feeds, one client each)
//        │  refresh every REFRESH_MS, and when the date lands on as-of
//        ▼
//   per provider: the last SUCCESSFUL answer stays until the provider answers again, so one
//   provider being down neither empties the list nor blocks the others
//        ▼
//   A provider is asked no more often than PROVIDER_GAP_MS says, and is left alone for
//   RATE_LIMITED_MS after answering HTTP 429; until then its last answer stays.
//
//   items (all of them, newest first)  +  cards for new items past their threshold:
//
//     quake       magnitude ≥ QUAKE_MIN_MAG                       priority 40 + 10 per magnitude above
//     alert       every GDACS orange / red alert                  priority 45 + 10 per alert score
//     event       EONET volcano                                   priority 40
//     chokepoint  traffic down ≥ CHOKEPOINT_MIN_LOSS              priority 50
//     launch      lifts off within LAUNCH_SOON_MS                 priority 40
//     headline    items only, never a card
//
// A provider failure is logged to the console and shows as that provider's items going stale;
// nothing is invented to fill the gap.

import { computed, signal } from '@preact/signals';
import { HEX_COUNT, hexAtLonLat } from '../core/grid.ts';
import type { FeedClients, LiveRecord } from '../data/index.ts';
import type { FeedService, LiveItem, Notification, NotificationKind } from './api.ts';
import { CARD_MIN, LIVE_ID_PREFIX, MAX_CARDS, MAX_PRIORITY, post } from './notifications.ts';
import type { Store } from './state.ts';

export const REFRESH_MS = 5 * 60 * 1000;
/** Smallest gap between two refreshes, however they are triggered. */
const MIN_GAP_MS = 30 * 1000;

/** Launch Library 2 rate-limits (HTTP 429), so launches are asked for less often than the rest. */
const PROVIDER_GAP_MS: Partial<Record<keyof FeedClients, number>> = { launches: 15 * 60 * 1000 };
/** How long a provider that answered HTTP 429 is left alone. */
const RATE_LIMITED_MS = 60 * 60 * 1000;
const RATE_LIMITED = /HTTP 429/;
/** 'YYYY-MM-DDTHH:MM…' → 'YYYY-MM-DD'. */
const DAY_CHARS = 10;

const QUAKE_MIN_MAG = 6;
const QUAKE_BASE = 40;
const QUAKE_PER_MAG = 10;
const ALERT_BASE = 45;
const ALERT_PER_SCORE = 10;
const VOLCANO_PRIORITY = 40;
const CHOKEPOINT_MIN_LOSS = 0.3;
const CHOKEPOINT_PRIORITY = 50;
const LAUNCH_SOON_MS = 24 * 60 * 60 * 1000;
const LAUNCH_PRIORITY = 40;

/** Which provider a record came from: each answers or fails as a whole. */
type Provider = keyof FeedClients;
const PROVIDERS: readonly Provider[] = ['quakes', 'alerts', 'events', 'chokepoints', 'launches', 'headlines'];

const DISASTER_KINDS: readonly LiveItem['kind'][] = ['quake', 'cyclone', 'flood', 'volcano', 'wildfire', 'drought'];

function toItem(r: LiveRecord): LiveItem {
  const placed = r.lat !== undefined && r.lon !== undefined;
  const hex = placed ? hexAtLonLat(r.lon as number, r.lat as number) : undefined;
  return { ...r, hex: hex !== undefined && hex >= 0 && hex < HEX_COUNT ? hex : undefined };
}

function kindOf(item: LiveItem): NotificationKind {
  if (DISASTER_KINDS.includes(item.kind)) {
    return 'disaster';
  }
  return item.kind === 'launch' ? 'launch' : 'event';
}

/** Card priority of a live item, or null when it is not notable. */
function priorityOf(item: LiveItem, provider: Provider, now: number): number | null {
  const severity = item.severity ?? 0;

  switch (provider) {
    case 'quakes':
      return severity >= QUAKE_MIN_MAG ? QUAKE_BASE + (severity - QUAKE_MIN_MAG) * QUAKE_PER_MAG : null;
    case 'alerts':
      return ALERT_BASE + severity * ALERT_PER_SCORE;
    case 'events':
      return item.kind === 'volcano' ? VOLCANO_PRIORITY : null;
    case 'chokepoints':
      return severity >= CHOKEPOINT_MIN_LOSS ? CHOKEPOINT_PRIORITY : null;
    case 'launches': {
      const until = Date.parse(item.time) - now;
      return until >= 0 && until <= LAUNCH_SOON_MS ? LAUNCH_PRIORITY : null;
    }
    default:
      return null;
  }
}

function toNote(item: LiveItem, priority: number): Notification {
  return {
    id: `${LIVE_ID_PREFIX}${item.id}`,
    kind: kindOf(item),
    date: item.time.slice(0, DAY_CHARS),
    title: item.title,
    body: `${item.publisher}, ${item.time}`,
    hex: item.hex,
    iso3s: [],
    sources: [],
    links: [item.url],
    priority: Math.max(CARD_MIN, Math.min(MAX_PRIORITY, Math.round(priority))),
  };
}

export interface FeedDeps {
  clients: FeedClients;
  store: Store;
  now?: () => number;
}

export interface Feeds {
  service: FeedService;
  /** Start the interval and the as-of trigger; returns a stop function. */
  start(): () => void;
}

export function createFeeds({ clients, store, now = Date.now }: FeedDeps): Feeds {
  const byProvider = signal<Map<Provider, LiveItem[]>>(new Map());
  const raised = new Set<string>();
  let last = -Infinity;
  /** Provider → the earliest time it may be asked again. */
  const notBefore = new Map<Provider, number>();
  let running: Promise<void> | null = null;

  const items = computed(() =>
    [...byProvider.value.values()].flat().sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0)),
  );

  /** Cards for items not raised before, best first; the rest are only listed. */
  function raiseNew(fresh: Array<{ item: LiveItem; provider: Provider }>): void {
    const notes: Notification[] = [];

    for (const { item, provider } of fresh) {
      const priority = priorityOf(item, provider, now());
      if (priority === null || raised.has(item.id)) {
        continue;
      }
      raised.add(item.id);
      notes.push(toNote(item, priority));
    }
    notes.sort((a, b) => b.priority - a.priority);
    post(store, { cards: notes.slice(0, MAX_CARDS), log: notes });
  }

  async function pull(): Promise<void> {
    const due = PROVIDERS.filter((p) => now() >= (notBefore.get(p) ?? -Infinity));
    due.forEach((p) => notBefore.set(p, now() + (PROVIDER_GAP_MS[p] ?? 0)));

    const answers = await Promise.allSettled(due.map((p) => clients[p]()));
    const next = new Map(byProvider.peek());
    const fresh: Array<{ item: LiveItem; provider: Provider }> = [];

    answers.forEach((answer, i) => {
      const provider = due[i];
      if (answer.status === 'rejected') {
        console.warn(`feeds: ${provider} failed: ${String(answer.reason)}`);
        if (RATE_LIMITED.test(String(answer.reason))) {
          notBefore.set(provider, now() + RATE_LIMITED_MS);
        }
        return;
      }
      const list = answer.value.map(toItem);
      next.set(provider, list);
      fresh.push(...list.map((item) => ({ item, provider })));
    });

    byProvider.value = next;
    raiseNew(fresh);
    last = now();
  }

  /** One refresh at a time; a call during a refresh joins it. */
  function refresh(): Promise<void> {
    running ??= pull().finally(() => {
      running = null;
    });
    return running;
  }

  function tick(): void {
    const live = store.date.peek() >= store.asOf.peek();
    if (!live || now() - last < MIN_GAP_MS) {
      return;
    }
    void refresh();
  }

  function start(): () => void {
    const timer = setInterval(tick, REFRESH_MS);
    // Landing on the as-of date (autoplay reaching today, the slider's end) refreshes at once.
    const stopDate = store.date.subscribe(tick);
    return () => {
      clearInterval(timer);
      stopDate();
    };
  }

  return { service: { items, refresh }, start };
}
